import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'
import { useFamilyStore } from '../stores/family'
import {
  ListTodo, Repeat2, Grid2X2, CalendarDays, Receipt,
  Zap, LogOut, ChevronRight, BookOpen, Timer, PenLine,
  Target, LayoutDashboard, BrainCircuit, BookMarked, Clock,
  Brain, Users, ChevronLeft, Home, UsersRound, CreditCard, Building2,
  Settings, BarChart3, Plug,
} from 'lucide-react'
import { isTenantAdmin, isSuperAdmin } from '../auth/roles'
import { useSidebar } from '../hooks/useSidebar'
import { ThemeToggle } from './ThemeToggle'
import { CurrentPlanBadge } from './CurrentPlanBadge'
import { Tooltip } from './ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { GlobalSearch } from './GlobalSearch'
import CookieConsent from './CookieConsent'

const navGroups = [
  {
    label: 'Workspace',
    items: [
      { to: '/',         label: 'Brain Dump', icon: Brain,          exact: true },
      { to: '/todos',    label: 'Todos',      icon: ListTodo                    },
      { to: '/habits',   label: 'Habits',     icon: Repeat2                     },
      { to: '/matrix',   label: 'Matrix',     icon: Grid2X2                     },
      { to: '/calendar', label: 'Calendar',   icon: CalendarDays                },
    ],
  },
  {
    label: 'Learning',
    items: [
      { to: '/feynman',    label: 'Feynman',    icon: BookOpen     },
      { to: '/flashcards', label: 'Flashcards', icon: BrainCircuit },
      { to: '/reading',    label: 'Reading',    icon: BookMarked   },
      { to: '/pomodoro',   label: 'Pomodoro',   icon: Timer        },
    ],
  },
  {
    label: 'Personal',
    items: [
      { to: '/expenses',      label: 'Expenses',      icon: Receipt         },
      { to: '/journal',       label: 'Journal',       icon: PenLine         },
      { to: '/goals',         label: 'Goals',         icon: Target          },
      { to: '/kanban',        label: 'Kanban',        icon: LayoutDashboard },
      { to: '/time-blocking', label: 'Time Blocking', icon: Clock           },
    ],
  },
]

const familyNavItems = [
  { to: '/family',          label: 'Family Hub',     icon: Home,            exact: true as const },
  { to: '/family/todos',    label: 'Family Todos',   icon: ListTodo                               },
  { to: '/family/calendar', label: 'Family Calendar',icon: CalendarDays                           },
  { to: '/family/kanban',   label: 'Family Kanban',  icon: LayoutDashboard                        },
  { to: '/family/goals',    label: 'Family Goals',   icon: Target                                 },
  { to: '/family/expenses', label: 'Family Expenses',icon: Receipt                                },
]

const allNavItems = navGroups.flatMap(g => g.items).concat(familyNavItems)

function getInitials(name: string) {
  const parts = name.split(/[\s@.]+/).filter(Boolean)
  return parts.length === 0 ? '?' : parts.slice(0, 2).map(p => p[0].toUpperCase()).join('')
}

function usePageTitle() {
  const { pathname } = useLocation()
  return allNavItems.find(n => n.exact ? pathname === n.to : pathname.startsWith(n.to))?.label ?? 'Behvy'
}

export default function Layout() {
  const logout   = useAuthStore(s => s.logout)
  const user     = useAuthStore(s => s.user)
  const navigate = useNavigate()
  const title    = usePageTitle()
  const { collapsed, toggle } = useSidebar()
  const { mode, setMode, space } = useFamilyStore()

  function handleLogout() {
    logout()
    import('../api/client').then(({ http }) => {
      import('../api/endpoints').then(({ Endpoints }) => {
        http.post(Endpoints.auth.logout).catch(() => {})
      })
    })
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">

      {/* ── Sidebar ── */}
      <aside
        className={`${collapsed ? 'w-14' : 'w-56'} shrink-0 flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300 ease-in-out overflow-hidden`}
      >
        {/* Brand */}
        <div className={`h-14 flex items-center shrink-0 border-b border-sidebar-border overflow-hidden ${collapsed ? 'justify-center' : 'gap-2.5 px-4'}`}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-sidebar-primary">
            <Zap className="w-4 h-4 shrink-0 text-sidebar-primary-foreground" strokeWidth={2.5} />
          </div>
          {!collapsed && (
            <span className="font-bold text-[15px] tracking-tight whitespace-nowrap text-sidebar-foreground">
              Behvy
            </span>
          )}
        </div>

        {/* Workspace switcher */}
        <div className={`border-b border-sidebar-border ${collapsed ? 'p-1' : 'px-3 py-2'}`}>
          {collapsed ? (
            <Tooltip content={mode === 'family' ? 'Family' : 'Personal'} side="right">
              <button
                onClick={() => setMode(mode === 'personal' ? 'family' : 'personal')}
                className="w-full flex justify-center p-1.5 rounded-md hover:bg-sidebar-accent transition-colors"
              >
                {mode === 'family'
                  ? <UsersRound className="w-4 h-4 text-sidebar-primary" />
                  : <Home className="w-4 h-4 text-sidebar-foreground/60" />}
              </button>
            </Tooltip>
          ) : (
            <div className="flex rounded-md overflow-hidden border border-sidebar-border text-[11px] font-semibold">
              <button
                onClick={() => setMode('personal')}
                className={`flex-1 py-1.5 transition-colors ${
                  mode === 'personal'
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                    : 'text-sidebar-foreground/55 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                }`}
              >
                Personal
              </button>
              <button
                onClick={() => setMode('family')}
                className={`flex-1 py-1.5 transition-colors ${
                  mode === 'family'
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                    : 'text-sidebar-foreground/55 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                }`}
              >
                {space ? space.name : 'Family'}
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden py-2 px-2">
          {navGroups.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? 'mt-3' : ''}>
              {!collapsed ? (
                <p className="text-[10px] font-semibold uppercase tracking-widest px-2 pb-1 pt-0.5 text-sidebar-foreground/35">
                  {group.label}
                </p>
              ) : (
                gi > 0 && <div className="border-t border-sidebar-border my-2" />
              )}
              {group.items.map(({ to, label, icon: Icon, exact }) => (
                <Tooltip key={to} content={label} side="right" disabled={!collapsed}>
                  <NavLink
                    to={to}
                    end={exact}
                    className={({ isActive }) =>
                      `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                        collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                      } ${
                        isActive
                          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                          : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {!collapsed && label}
                  </NavLink>
                </Tooltip>
              ))}
            </div>
          ))}

          {/* Family nav */}
          {mode === 'family' && (
            <div className="mt-3">
              {!collapsed ? (
                <p className="text-[10px] font-semibold uppercase tracking-widest px-2 pb-1 pt-0.5 text-sidebar-foreground/35">
                  Family
                </p>
              ) : (
                <div className="border-t border-sidebar-border my-2" />
              )}
              {familyNavItems.map(({ to, label, icon: Icon, exact }) => (
                <Tooltip key={to} content={label} side="right" disabled={!collapsed}>
                  <NavLink
                    to={to}
                    end={exact}
                    className={({ isActive }) =>
                      `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                        collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                      } ${
                        isActive
                          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                          : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {!collapsed && label}
                  </NavLink>
                </Tooltip>
              ))}
            </div>
          )}

          {/* ── SuperAdmin Section ── */}
          {/* Justification: SuperAdmin-only features for managing the entire platform */}
          {isSuperAdmin(user?.role) && (
            <div className="mt-3">
              {!collapsed ? (
                <p className="text-[10px] font-semibold uppercase tracking-widest px-2 pb-1 pt-0.5 text-sidebar-foreground/35">
                  System Admin
                </p>
              ) : (
                <div className="border-t border-sidebar-border my-2" />
              )}
              <Tooltip content="All Users" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/all-users"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <Users className="w-4 h-4 shrink-0" />
                  {!collapsed && 'All Users'}
                </NavLink>
              </Tooltip>
              <Tooltip content="Plans" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/plans"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <CreditCard className="w-4 h-4 shrink-0" />
                  {!collapsed && 'Plans'}
                </NavLink>
              </Tooltip>
              <Tooltip content="Tenants" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/tenants"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <Building2 className="w-4 h-4 shrink-0" />
                  {!collapsed && 'Tenants'}
                </NavLink>
              </Tooltip>
              <Tooltip content="System Settings" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/settings"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <Settings className="w-4 h-4 shrink-0" />
                  {!collapsed && 'System Settings'}
                </NavLink>
              </Tooltip>
              <Tooltip content="Analytics" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/analytics"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <BarChart3 className="w-4 h-4 shrink-0" />
                  {!collapsed && 'Analytics'}
                </NavLink>
              </Tooltip>
              <Tooltip content="Integrations" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/integrations"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <Plug className="w-4 h-4 shrink-0" />
                  {!collapsed && 'Integrations'}
                </NavLink>
              </Tooltip>
            </div>
          )}

          {/* ── Tenant Admin Section ── */}
          {/* Justification: TenantAdmin features for managing their own organization */}
          {isTenantAdmin(user?.role) && (
            <div className="mt-3">
              {!collapsed ? (
                <p className="text-[10px] font-semibold uppercase tracking-widest px-2 pb-1 pt-0.5 text-sidebar-foreground/35">
                  {isSuperAdmin(user?.role) ? 'Tenant Admin' : 'Admin'}
                </p>
              ) : (
                <div className="border-t border-sidebar-border my-2" />
              )}
              <Tooltip content="Team" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <Users className="w-4 h-4 shrink-0" />
                  {!collapsed && 'Team'}
                </NavLink>
              </Tooltip>
              <Tooltip content="Tenant Settings" side="right" disabled={!collapsed}>
                <NavLink
                  to="/admin/tenant-settings"
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-[13px] font-medium transition-colors duration-150 mb-0.5 ${
                      collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 py-2'
                    } ${
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`
                  }
                >
                  <Settings className="w-4 h-4 shrink-0" />
                  {!collapsed && 'Tenant Settings'}
                </NavLink>
              </Tooltip>
            </div>
          )}
        </nav>

        {/* Current Plan Badge */}
        {!collapsed && (
          <div className="px-2 pb-2">
            <CurrentPlanBadge />
          </div>
        )}

        {/* Footer */}
        <div className="shrink-0 border-t border-sidebar-border">
          {/* Legal links */}
          {!collapsed && (
            <div className="px-3 py-2 border-b border-sidebar-border">
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-sidebar-foreground/40">
                <a href="/terms" className="hover:text-sidebar-foreground/70 transition-colors">Terms</a>
                <a href="/privacy" className="hover:text-sidebar-foreground/70 transition-colors">Privacy</a>
                <a href="/cookies" className="hover:text-sidebar-foreground/70 transition-colors">Cookies</a>
              </div>
            </div>
          )}
          {/* Collapse toggle */}
          <button
            onClick={toggle}
            className={`w-full flex items-center py-2.5 text-xs font-medium text-sidebar-foreground/50 hover:text-sidebar-foreground/90 hover:bg-sidebar-accent transition-colors ${
              collapsed ? 'justify-center' : 'gap-2 px-3'
            }`}
          >
            {collapsed
              ? <ChevronRight className="w-4 h-4" />
              : <><ChevronLeft className="w-3.5 h-3.5" /><span>Collapse</span></>
            }
          </button>
          {/* User menu */}
          <div className="px-2 pb-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`w-full flex items-center rounded-md hover:bg-sidebar-accent transition-colors py-1.5 ${collapsed ? 'justify-center' : 'gap-2.5 px-2'}`}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold bg-sidebar-accent text-sidebar-accent-foreground">
                    {getInitials(user?.name ?? 'U')}
                  </div>
                  {!collapsed && (
                    <p className="text-[12px] font-medium truncate flex-1 min-w-0 text-left text-sidebar-foreground/75">
                      {user?.name}
                    </p>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align={collapsed ? 'center' : 'end'} className="w-48">
                <DropdownMenuItem disabled>
                  <span className="text-xs text-muted-foreground truncate">{user?.name}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  className="cursor-pointer"
                  onClick={handleLogout}
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* ── Content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 shrink-0 flex items-center justify-between px-6 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Behvy</span>
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">{title}</span>
          </div>
          <ThemeToggle />
        </header>
        <main className="flex-1 overflow-auto">
          <div className="p-8 h-full flex flex-col">
            <Outlet />
          </div>
        </main>
      </div>
      <GlobalSearch />
      <CookieConsent />
    </div>
  )
}


